import { useState, useEffect, useRef, useCallback } from "react";
import { DepositOSWidget } from "@gizmolab/depositos";
import type { DepositOSConfig } from "@gizmolab/depositos";
import {
  parseConfigFromURL,
  parseRenderTypeFromURL,
  parseLauncherButtonTextFromURL,
  isValidConfig,
  type IframeRenderType,
} from "./lib/config";
import { postToParent, onParentMessage } from "./lib/messages";

function useResizeReporter(ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        postToParent({ type: "depositos:resize", height: Math.ceil(entry.contentRect.height) });
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
}

function ErrorState({ message }: { message: string }) {
  useEffect(() => {
    postToParent({ type: "depositos:error", error: message });
  }, [message]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 200,
        padding: 24,
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: "rgba(255,255,255,0.5)",
        textAlign: "center",
        fontSize: 14,
      }}
    >
      <div>
        <p style={{ fontWeight: 600, marginBottom: 8 }}>depositOS</p>
        <p>{message}</p>
        <p style={{ marginTop: 12, fontSize: 12, opacity: 0.6 }}>
          Pass config via URL params or <code>postMessage</code>.
        </p>
      </div>
    </div>
  );
}

export default function App() {
  const [config, setConfig] = useState<Partial<DepositOSConfig>>(() => parseConfigFromURL());
  const [renderType, setRenderType] = useState<IframeRenderType>(() => parseRenderTypeFromURL());
  const [launcherButtonText, setLauncherButtonText] = useState<string>(() => parseLauncherButtonTextFromURL());
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(() => parseRenderTypeFromURL() === "popup");
  const containerRef = useRef<HTMLDivElement>(null);
  const valid = isValidConfig(config);

  useResizeReporter(containerRef);

  useEffect(() => {
    if (renderType === "popup") setIsPopupOpen(true);
    if (renderType === "inline") setIsPopupOpen(false);
  }, [renderType]);

  useEffect(() => {
    if (!isPopupOpen) return;
    const pushFullscreenHeight = () => {
      postToParent({ type: "depositos:resize", height: window.innerHeight });
    };
    pushFullscreenHeight();
    window.addEventListener("resize", pushFullscreenHeight);
    return () => window.removeEventListener("resize", pushFullscreenHeight);
  }, [isPopupOpen]);

  useEffect(() => {
    return onParentMessage((msg) => {
      if (msg.type === "depositos:config") {
        const {
          integratorId: _ignoredIntegrator,
          dynamicEnvironmentId: _ignoredDynamicEnv,
          ...safeConfig
        } = msg.config;
        setConfig((prev) => ({ ...prev, ...safeConfig, enableLayerswap: false }));
      }
      if (msg.type === "depositos:displayType") {
        setRenderType(msg.displayType);
        if (typeof msg.buttonText === "string" && msg.buttonText.trim().length > 0) {
          setLauncherButtonText(msg.buttonText.trim());
        }
      }
    });
  }, []);

  useEffect(() => {
    postToParent({ type: "depositos:ready" });
  }, []);

  const handleClose = useCallback(() => {
    setIsPopupOpen(false);
    postToParent({ type: "depositos:close" });
  }, []);

  const handleSuccess = useCallback((txHash?: string) => {
    postToParent({ type: "depositos:success", txHash });
  }, []);

  return (
    <div
      ref={containerRef}
      style={isPopupOpen ? { minHeight: "100vh", width: "100%" } : undefined}
    >
      {valid ? (
        renderType === "inline" ? (
          <DepositOSWidget
            config={config}
            onClose={handleClose}
            onSuccess={handleSuccess}
          />
        ) : (
          <>
            {renderType === "button" && !isPopupOpen && (
              <button
                type="button"
                onClick={() => setIsPopupOpen(true)}
                style={{
                  background: "linear-gradient(135deg,#10b981,#3b82f6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 12,
                  padding: "12px 18px",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                {launcherButtonText}
              </button>
            )}
            {isPopupOpen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 16,
                  backgroundColor: "rgba(0,0,0,0.01)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                }}
              >
                <div style={{ width: 420, maxWidth: "100%", maxHeight: "calc(100vh - 2rem)", overflowY: "auto" }}>
                  <DepositOSWidget
                    config={config}
                    onClose={handleClose}
                    onSuccess={handleSuccess}
                  />
                </div>
              </div>
            )}
          </>
        )
      ) : (
        <ErrorState message="Missing required config: destChain, destToken, destAddress" />
      )}
    </div>
  );
}
